import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { api, friendlyError } from '../../lib/api-client';
import { changeAppLanguage } from '../../i18n/i18n';
import { SUPPORTED_LOCALES, type AppLocale } from '../../i18n/localeTypes';
import { getShellDocumentTitle, getShellPageContext } from '../../lib/app-page-context';
import AppSidebar from './AppSidebar';
import AppBroadcastStack from './AppBroadcastStack';
import HelpCopilotDrawer from '../help/HelpCopilotDrawer';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import AlertBanner from '../ui/AlertBanner';
import BrandLogo from '../brand/BrandLogo';

const SIDEBAR_STORAGE_KEY = 'ablecomply_sidebar_collapsed';
const THEME_STORAGE_KEY = 'ablecomply_theme_mode';

type ThemeMode = 'light' | 'dark';

function getUserInitials(input: { fullName?: string | null; email?: string | null }): string {
  const fullName = input.fullName?.trim();
  if (fullName) {
    const parts = fullName.split(/\s+/u).filter(Boolean).slice(0, 2);
    const initials = parts.map((part) => part[0]?.toUpperCase() ?? '').join('');
    if (initials) {
      return initials;
    }
  }

  const email = input.email?.trim();
  if (email) {
    return email.slice(0, 2).toUpperCase();
  }

  return 'AC';
}

function getPartnerSectionLabel(pathname: string, t: (key: string) => string): string {
  if (pathname.startsWith('/partner/referrals')) {
    return t('partnerPortal:shell.sections.referrals');
  }

  if (pathname.startsWith('/partner/commissions')) {
    return t('partnerPortal:shell.sections.commissions');
  }

  if (pathname.startsWith('/partner/payouts')) {
    return t('partnerPortal:shell.sections.payouts');
  }

  if (pathname.startsWith('/partner/settings')) {
    return t('partnerPortal:shell.sections.settings');
  }

  return t('partnerPortal:shell.sections.dashboard');
}

function slugifyWorkspaceName(name: string): string {
  const normalized = name
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

  if (!normalized) {
    return '';
  }

  if (normalized.length >= 3) {
    return normalized.slice(0, 63);
  }

  return `${normalized}-workspace`.slice(0, 63);
}

export default function AppShellLayout() {
  const { t, i18n } = useTranslation(['common', 'navigation', 'help', 'partnerPortal', 'workspace']);
  const { user, org, role, workspaces, partnerAccess, logout, syncLocalePreferences, switchWorkspace, createWorkspace } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isPartnerExperience = partnerAccess !== null && location.pathname.startsWith('/partner');
  const hasPartnerMode = partnerAccess !== null;
  const activeMode = isPartnerExperience ? 'partner' : 'workspace';
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
  });
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') {
      return 'light';
    }

    return window.localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  });
  const [alertsEnabled, setAlertsEnabled] = useState<boolean | null>(null);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [workspaceModalOpen, setWorkspaceModalOpen] = useState(false);
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceSlug, setWorkspaceSlug] = useState('');
  const [workspaceSlugDirty, setWorkspaceSlugDirty] = useState(false);
  const [workspaceActionFeedback, setWorkspaceActionFeedback] = useState<{
    tone: 'success' | 'danger';
    message: string;
  } | null>(null);
  const [switchingWorkspaceId, setSwitchingWorkspaceId] = useState<string | null>(null);
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const context = useMemo(
    () => getShellPageContext(location.pathname, location.search, t),
    [location.pathname, location.search, t],
  );
  const shellDocumentTitle = useMemo(
    () => getShellDocumentTitle(location.pathname, location.search, t),
    [location.pathname, location.search, t],
  );
  const partnerSectionLabel = useMemo(
    () => (isPartnerExperience ? getPartnerSectionLabel(location.pathname, t) : null),
    [isPartnerExperience, location.pathname, t],
  );
  const userInitials = useMemo(
    () => getUserInitials({ fullName: user?.fullName, email: user?.email }),
    [user?.email, user?.fullName],
  );
  const hasMultipleWorkspaces = workspaces.length > 1;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? 'true' : 'false');
  }, [collapsed]);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    if (themeMode === 'dark') {
      document.documentElement.dataset.theme = 'dark';
    } else {
      delete document.documentElement.dataset.theme;
    }

    window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);

    return () => {
      delete document.documentElement.dataset.theme;
    };
  }, [themeMode]);

  useEffect(() => {
    if (workspaceSlugDirty) {
      return;
    }

    setWorkspaceSlug(slugifyWorkspaceName(workspaceName));
  }, [workspaceName, workspaceSlugDirty]);

  useEffect(() => {
    if (isPartnerExperience) {
      setAlertsEnabled(null);
      return;
    }

    const loadUsage = async () => {
      try {
        const data = await api.get<{ limits: { alertsEnabled: boolean } }>('/orgs/me/usage');
        setAlertsEnabled(data.limits.alertsEnabled);
      } catch {
        setAlertsEnabled(true);
      }
    };

    void loadUsage();
  }, [isPartnerExperience]);

  useEffect(() => {
    if (isPartnerExperience) {
      setUnreadAlerts(0);
      return;
    }

    if (alertsEnabled === false) {
      setUnreadAlerts(0);
      return;
    }

    const fetchCount = async () => {
      try {
        const data = await api.get<{ count: number }>('/alerts/unread-count');
        setUnreadAlerts(data.count ?? 0);
      } catch {
        // Non-blocking shell signal.
      }
    };

    void fetchCount();
    const timer = window.setInterval(() => void fetchCount(), 60_000);
    return () => window.clearInterval(timer);
  }, [alertsEnabled, isPartnerExperience]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.title = `${shellDocumentTitle} - ${t('brand')}`;
  }, [shellDocumentTitle, t]);

  const handleLanguageChange = async (nextLocale: string) => {
    await changeAppLanguage(nextLocale);
    syncLocalePreferences({ preferredLocale: nextLocale as AppLocale });
    try {
      await api.patch('/auth/me/preferences', {
        preferredLocale: nextLocale,
      });
    } catch {
      // Locale still persists locally.
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleModeChange = (nextMode: 'workspace' | 'partner') => {
    if (activeMode === nextMode) {
      return;
    }

    navigate(nextMode === 'partner' ? '/partner/dashboard' : '/dashboard');
  };

  const toggleThemeMode = () => {
    setThemeMode((current) => (current === 'dark' ? 'light' : 'dark'));
  };

  const openWorkspaceModal = () => {
    setWorkspaceActionFeedback(null);
    setWorkspaceName('');
    setWorkspaceSlug('');
    setWorkspaceSlugDirty(false);
    setWorkspaceModalOpen(true);
  };

  const closeWorkspaceModal = () => {
    setWorkspaceModalOpen(false);
    setWorkspaceActionFeedback(null);
  };

  const handleWorkspaceSwitch = async (organisationId: string) => {
    if (!org || organisationId === org.id) {
      return;
    }

    setWorkspaceActionFeedback(null);
    setSwitchingWorkspaceId(organisationId);
    try {
      await switchWorkspace(organisationId);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      setWorkspaceActionFeedback({
        tone: 'danger',
        message: friendlyError(error, t('workspace:workspaceSwitcher.switchFailed')),
      });
    } finally {
      setSwitchingWorkspaceId(null);
    }
  };

  const handleCreateWorkspace = async () => {
    const trimmedName = workspaceName.trim();
    const trimmedSlug = workspaceSlug.trim();
    const slugPattern = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/u;

    if (!trimmedName) {
      setWorkspaceActionFeedback({
        tone: 'danger',
        message: t('workspace:workspaceSwitcher.errors.nameRequired'),
      });
      return;
    }

    if (trimmedSlug && (trimmedSlug.length < 3 || trimmedSlug.length > 63 || !slugPattern.test(trimmedSlug))) {
      setWorkspaceActionFeedback({
        tone: 'danger',
        message: t('workspace:workspaceSwitcher.errors.slugInvalid'),
      });
      return;
    }

    setWorkspaceActionFeedback(null);
    setCreatingWorkspace(true);
    try {
      await createWorkspace({
        organisationName: trimmedName,
        organisationSlug: trimmedSlug || undefined,
      });
      setWorkspaceModalOpen(false);
      setWorkspaceName('');
      setWorkspaceSlug('');
      setWorkspaceSlugDirty(false);
      setWorkspaceActionFeedback({
        tone: 'success',
        message: t('workspace:workspaceSwitcher.createSuccess', { name: trimmedName }),
      });
      navigate('/dashboard', { replace: true });
    } catch (error) {
      setWorkspaceActionFeedback({
        tone: 'danger',
        message: friendlyError(error, t('workspace:workspaceSwitcher.createFailed')),
      });
    } finally {
      setCreatingWorkspace(false);
    }
  };

  const themeToggleLabel = themeMode === 'dark'
    ? t('shell.theme.darkMode')
    : t('shell.theme.lightMode');
  const themeToggleActionLabel = themeMode === 'dark'
    ? t('shell.theme.switchToLight')
    : t('shell.theme.switchToDark');
  const themeToggleIcon = themeMode === 'dark' ? '☾' : '☀';

  return (
    <div className={collapsed ? 'acc-shell acc-shell--collapsed' : 'acc-shell'}>
      <AppSidebar
        collapsed={collapsed}
        unreadAlerts={unreadAlerts}
        alertsEnabled={alertsEnabled}
      />

      <div className="acc-shell__main">
        <header className={isPartnerExperience ? 'acc-shell__topbar acc-shell__topbar--partner' : 'acc-shell__topbar'}>
          <div className="acc-shell__topbar-left">
            <button
              type="button"
              className="acc-shell__collapse-button"
              onClick={() => setCollapsed((current) => !current)}
              aria-label={collapsed ? t('shell.expandSidebar') : t('shell.collapseSidebar')}
            >
              {collapsed ? '→' : '←'}
            </button>
            <div className="acc-shell__context-mark" aria-hidden="true">
              <BrandLogo variant="icon" tone={themeMode === 'dark' ? 'light' : 'dark'} />
            </div>
            <div className="acc-shell__context">
              <span className="acc-shell__context-label">
                {isPartnerExperience ? t('partnerPortal:shell.headerEyebrow') : context.label}
              </span>
              <div className="acc-shell__context-row">
                <strong className="acc-shell__context-title">
                  {isPartnerExperience ? partnerAccess?.partnerName : context.detail}
                </strong>
                <span className="acc-shell__context-separator" aria-hidden="true">
                  ·
                </span>
                <span className="acc-shell__context-subtitle">
                  {isPartnerExperience ? partnerSectionLabel : t('shell.context.subtitle')}
                </span>
              </div>
            </div>
            {hasPartnerMode && (
              <div className="acc-shell__mode-switcher" role="group" aria-label={t('shell.modeSwitcher.label')}>
                <button
                  type="button"
                  className={
                    activeMode === 'workspace'
                      ? 'acc-shell__mode-option acc-shell__mode-option--active'
                      : 'acc-shell__mode-option'
                  }
                  onClick={() => handleModeChange('workspace')}
                  aria-pressed={activeMode === 'workspace'}
                >
                  {t('shell.modeSwitcher.workspace')}
                </button>
                <button
                  type="button"
                  className={
                    activeMode === 'partner'
                      ? 'acc-shell__mode-option acc-shell__mode-option--active'
                      : 'acc-shell__mode-option'
                  }
                  onClick={() => handleModeChange('partner')}
                  aria-pressed={activeMode === 'partner'}
                >
                  {t('shell.modeSwitcher.partner')}
                </button>
              </div>
            )}
          </div>

          {isPartnerExperience ? (
            <div className="acc-shell__topbar-actions acc-shell__topbar-actions--partner">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="acc-shell__theme-toggle"
                onClick={toggleThemeMode}
                aria-pressed={themeMode === 'dark'}
                aria-label={themeToggleActionLabel}
                title={themeToggleActionLabel}
              >
                <span aria-hidden="true">{themeToggleIcon}</span>
                <span>{themeToggleLabel}</span>
              </Button>
              <div className="acc-partner-shell__identity" title={user?.email ?? undefined}>
                <div className="acc-partner-shell__avatar" aria-hidden="true">
                  {userInitials}
                </div>
                <div className="acc-partner-shell__identity-copy">
                  <div className="acc-partner-shell__identity-title">
                    {user?.fullName ?? partnerAccess?.partnerName}
                  </div>
                  <div className="acc-partner-shell__identity-subtitle">
                    {user?.email ?? t('partnerPortal:shell.memberFallback')}
                  </div>
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => void handleLogout()}>
                {t('navigation:signOut')}
              </Button>
            </div>
          ) : (
            <div className="acc-shell__topbar-actions">
              {org?.name && (
                <div className="acc-shell__workspace-controls">
                  <div className="acc-shell__workspace-summary">
                    <span className="acc-shell__workspace-summary-label">
                      {t('workspace:workspaceSwitcher.label')}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span className="acc-shell__meta-pill" title={org.name}>
                        {org.name}
                      </span>
                      {org.isDemoWorkspace && (
                        <Badge variant="warning">
                          Demo workspace
                        </Badge>
                      )}
                    </div>
                  </div>
                  {hasMultipleWorkspaces && (
                    <label className="acc-shell__workspace-select-wrap">
                      <span className="acc-shell__workspace-select-label">
                        {t('workspace:workspaceSwitcher.switchLabel')}
                      </span>
                      <select
                        value={org.id}
                        onChange={(event) => void handleWorkspaceSwitch(event.target.value)}
                        disabled={switchingWorkspaceId !== null}
                        className="acc-shell__workspace-select"
                      >
                        {workspaces.map((workspace) => (
                          <option key={workspace.organisation.id} value={workspace.organisation.id}>
                            {workspace.organisation.name} · {t(`common:roles.${workspace.role}`)}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={openWorkspaceModal}
                    disabled={creatingWorkspace}
                  >
                    {t('workspace:workspaceSwitcher.createAction')}
                  </Button>
                </div>
              )}
              {role && (
                <Badge variant="info" style={{ textTransform: 'capitalize' }}>
                  {t(`roles.${role}`)}
                </Badge>
              )}
              <Button size="sm" onClick={() => navigate('/sites/new')} data-add-site-trigger="true">
                {t('navigation:addSite')}
              </Button>
              <Link to="/alerts" className="acc-shell__quick-link">
                {t('navigation:reviewAlerts')}
                {unreadAlerts > 0 ? (
                  <span className="acc-shell__quick-link-badge">
                    {unreadAlerts > 99 ? '99+' : unreadAlerts}
                  </span>
                ) : null}
              </Link>
              <Button
                variant="secondary"
                size="sm"
                className="acc-shell__help-button"
                onClick={() => setHelpOpen(true)}
              >
                {t('help:launcher')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="acc-shell__theme-toggle"
                onClick={toggleThemeMode}
                aria-pressed={themeMode === 'dark'}
                aria-label={themeToggleActionLabel}
                title={themeToggleActionLabel}
              >
                <span aria-hidden="true">{themeToggleIcon}</span>
                <span>{themeToggleLabel}</span>
              </Button>
              <label className="acc-shell__locale">
                <span>{t('languageLabel')}</span>
                <select
                  value={i18n.resolvedLanguage ?? i18n.language}
                  onChange={(event) => void handleLanguageChange(event.target.value)}
                  className="acc-shell__locale-select"
                >
                  {SUPPORTED_LOCALES.map((locale) => (
                    <option key={locale} value={locale}>
                      {t(`locales.${locale as AppLocale}`)}
                    </option>
                  ))}
                </select>
              </label>
              {user?.email && (
                <span className="acc-shell__user" title={user.email}>
                  {user.email}
                </span>
              )}
              <Button variant="secondary" size="sm" onClick={() => void handleLogout()}>
                {t('navigation:signOut')}
              </Button>
            </div>
          )}
        </header>

        {workspaceActionFeedback && !isPartnerExperience && !workspaceModalOpen && (
          <div style={{ padding: '14px 28px 0' }}>
            <AlertBanner variant={workspaceActionFeedback.tone} icon={workspaceActionFeedback.tone === 'success' ? '✓' : '!'}>
              {workspaceActionFeedback.message}
            </AlertBanner>
          </div>
        )}

        <AppBroadcastStack />

        <main className="acc-shell__content">
          <Outlet />
        </main>
      </div>

      {!isPartnerExperience && (
        <HelpCopilotDrawer
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
        />
      )}

      {workspaceModalOpen && !isPartnerExperience && (
        <div className="acc-workspace-modal" role="dialog" aria-modal="true" aria-labelledby="acc-create-workspace-title">
          <button
            type="button"
            className="acc-workspace-modal__backdrop"
            onClick={closeWorkspaceModal}
            aria-label={t('workspace:workspaceSwitcher.close')}
          />
          <div className="acc-workspace-modal__panel">
            <div className="acc-workspace-modal__eyebrow">
              {t('workspace:workspaceSwitcher.eyebrow')}
            </div>
            <h2 id="acc-create-workspace-title" className="acc-workspace-modal__title">
              {t('workspace:workspaceSwitcher.dialogTitle')}
            </h2>
            <p className="acc-workspace-modal__description">
              {t('workspace:workspaceSwitcher.dialogDescription')}
            </p>

            {workspaceActionFeedback?.tone === 'danger' && (
              <AlertBanner variant="danger" icon="!" style={{ marginTop: 4 }}>
                {workspaceActionFeedback.message}
              </AlertBanner>
            )}

            <label className="acc-workspace-modal__field">
              <span className="acc-workspace-modal__label">{t('workspace:workspaceSwitcher.nameLabel')}</span>
              <input
                className="acc-input"
                type="text"
                value={workspaceName}
                onChange={(event) => setWorkspaceName(event.target.value)}
                placeholder={t('workspace:workspaceSwitcher.namePlaceholder')}
                autoComplete="off"
              />
            </label>

            <label className="acc-workspace-modal__field">
              <span className="acc-workspace-modal__label">{t('workspace:workspaceSwitcher.slugLabel')}</span>
              <input
                className="acc-input"
                type="text"
                value={workspaceSlug}
                onChange={(event) => {
                  setWorkspaceSlug(event.target.value.toLowerCase());
                  setWorkspaceSlugDirty(true);
                }}
                placeholder={t('workspace:workspaceSwitcher.slugPlaceholder')}
                autoComplete="off"
              />
            </label>

            <div className="acc-workspace-modal__hint">
              {t('workspace:workspaceSwitcher.slugHint')}
            </div>
            <div className="acc-workspace-modal__hint">
              {t('workspace:workspaceSwitcher.freePlanHint')}
            </div>

            <div className="acc-workspace-modal__actions">
              <Button variant="secondary" type="button" onClick={closeWorkspaceModal} disabled={creatingWorkspace}>
                {t('workspace:workspaceSwitcher.cancel')}
              </Button>
              <Button type="button" onClick={() => void handleCreateWorkspace()} disabled={creatingWorkspace}>
                {creatingWorkspace
                  ? t('workspace:workspaceSwitcher.submitting')
                  : t('workspace:workspaceSwitcher.submit')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
